/* eslint-disable no-nested-ternary */
import { Editor } from '@ckeditor/ckeditor5-core';
import { AxiosError } from 'axios';
import _ from 'lodash';
import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useClickAway } from 'react-use';
import { useGetLoginUserInfo } from '../../../../api/auth/auth';
import { ErrorInfo, PsDerivationContent } from '../../../../api/model';
import { usePostPsContent, usePostPsContentcheck } from '../../../../api/pss/pss';
import { PsContext } from '../../../../contexts/PsContext';
import { useDefaultOptions } from '../../../../hooks/api';
import useContextMenu from '../../../../hooks/useContextMenu';
import { getAxiosRequestConfig } from '../../../../utils/api';
import { toString } from '../../../../utils/string';
import { CheckBox } from '../../../atoms/CheckBox';
import DraggableModelessDialog from '../../../modules/DraggableModelessDialog';
import ErrorModal from '../../../modules/ErrorModal';
import SpinnerModal from '../../../modules/SpinnerModal';
import CKEditorAdapter from './CKEditor/js/CKEditorAdapter';
import { DiffType, PsDetailsMode } from './constants/types';
import { getDiffBackgroundColor } from './PsDetails';
import PsDiffViewerDialog from './PsDiffViewerDialog';

interface Props {
  /** PS 閲覧・追加・編集の内、どのモードかを示す。 */
  mode: PsDetailsMode;

  /** ps.derivations の index。 */
  psDerivationIndex: number;

  /** ps.derivations.derivation_contents の index。 */
  derivationContentIndex: number;

  /** CKFinder に渡す用の PS バージョン ID。 */
  psVersionId: number;

  /** PsDerivationContent.derivation_content_id と DiffType の Map。 */
  psDerivationContentIdDiffTypeMap: Map<number, DiffType>;
  /** PsDerivationContent.derivation_content_id と xml の Map。 */
  psDerivationContentIdDiffXmlMap: Map<number, string>;

  /** useFieldArray の update メソッド。 */
  // update: (index: number, obj: object) => void

  /** PSのコピーペースト用情報。 */
  psNo: string | undefined;
  psDerivationId: number | undefined;
  fsrId: string | undefined;
  psType: number | undefined;
  psLevel: number | undefined;
}

// CKEditor をコンテンツ XML ごとに表示するために切り分けた。
/** PsDerivationRow に含まれるコンポーネント。派生コンテンツ XML のセル。 */
function PsDerivationContentXmlCell({
  mode,
  psDerivationIndex,
  derivationContentIndex,
  psVersionId,
  psDerivationContentIdDiffTypeMap,
  psDerivationContentIdDiffXmlMap,
  psNo,
  psDerivationId,
  fsrId,
  psType,
  psLevel,
}: Props) {
  // i18n 用。
  const { t, i18n } = useTranslation();
  const axiosConfig = getAxiosRequestConfig(i18n.language);
  const options = useDefaultOptions(i18n.language);

  // PS コンテンツ変換 API。
  const postPsContentMutation = usePostPsContent({ axios: axiosConfig });

  // api処理中フラグ
  const [apiProcessing, setApiProcessing] = useState(false);

  // 保存ロジック
  const [displayCheckError, setDisplayCheckError] = useState(false);
  const {
    mutateAsync: psCheckMutateAsync,
    isLoading: checkIsLoding,
    error: checkError,
  } = usePostPsContentcheck<AxiosError<ErrorInfo>, unknown>(options);
  useEffect(() => {
    setDisplayCheckError(checkError != null);
  }, [checkError]);

  // console.log('postPsContentMutation:', postPsContentMutation);

  /** React Hook Form 用の、このコンポーネントで扱う derivation_content を指す name。 */
  const derivationContentName = `derivations.${psDerivationIndex}.derivation_contents.${derivationContentIndex}`;

  // 参照: https://qiita.com/y-yasuda/items/9184380d3320d47bc564
  // PsDetails に置かれた FormProvider から取得。
  const { register, control, getValues, setValue } = useFormContext();

  /** watch する content_text。 */
  const contentText = useWatch({
    control,
    name: `${derivationContentName}.content_text`,
  });

  /** watch する content_xml。 */
  const contentXml = useWatch({
    control,
    name: `${derivationContentName}.content_xml`,
  });
  // const contentXml = watch(`${derivationContentName}.content_xml`);

  // console.log('contentXml:', contentXml);

  /** 無限ループ防止用に、Mutation の status を比較用に保存する。 */
  const [previousPostPsContentMutationStatus, setPreviousPostPsContentMutationStatus] = useState<string>('');

  useEffect(() => {
    if (
      // loading → success になった時のみ実行。
      previousPostPsContentMutationStatus === 'loading' &&
      postPsContentMutation.status === 'success' &&
      postPsContentMutation.data?.data
    ) {
      // CKEditor で編集された XML を画像表示用に img 要素の src 属性値を署名付き URL に置換した XML とテキストが返ってくる。
      // 変換後コンテンツ テキスト を content_text に set する。
      setValue(
        `${derivationContentName}.content_text` as never,
        postPsContentMutation.data.data.content_text as never,
        {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        }
      );

      // 変換後コンテンツ XML を content_xml に set する。
      setValue(`${derivationContentName}.content_xml` as never, postPsContentMutation.data.data.content_xml as never, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });

      // 変換後コンテンツ XML (編集用) を content_xml_edit に set する。
      setValue(
        `${derivationContentName}.content_xml_edit` as never,
        postPsContentMutation.data.data.content_xml_edit as never,
        {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        }
      );
    }

    if (previousPostPsContentMutationStatus !== postPsContentMutation.status) {
      // 比較用に保存。
      setPreviousPostPsContentMutationStatus(postPsContentMutation.status);
    }

    // console.log('point2で閉じる');
    setDoShowCKEditor(false);
  }, [
    derivationContentName,
    postPsContentMutation.data?.data,
    postPsContentMutation.status,
    previousPostPsContentMutationStatus,
    setValue,
  ]);

  /** watch する content_xml_edit。 */
  const contentXmlEdit = useWatch({
    control,
    name: `${derivationContentName}.content_xml_edit`,
  });

  // console.log('contentXmlEdit:', contentXmlEdit);

  /** watch する unnecessary。 */
  const unnecessary = useWatch({
    control,
    name: `${derivationContentName}.unnecessary`,
  });

  // TODO?: update で xml と text を '記載不要' にアップデート。しかしそうすると、間違ってチェックボックスを押してしまうと、リロードなどをしないと以前の xml に戻せなくなってしまうから不便か。
  // useEffect(
  //   () => {
  //     if (unnecessary) {
  //       update(
  //         derivationContentIndex,
  //         {
  //           derivation_content_id: getValues(),
  //           represent: yup.boolean().nullable().default(null),
  //           unnecessary: yup.boolean().nullable().default(null),
  //           content_xml: yup.string().nullable().default(null),
  //           content_text: yup.string().nullable().default(null),
  //         }
  //       );
  //     }
  //   },
  //   [unnecessary]
  // );

  // start: CKEditor 関連。
  const [editor, setEditor] = useState<Editor | undefined>(undefined);

  /** CKEditor (のモーダル) を表示するか否か。 */
  const [doShowCKEditor, setDoShowCKEditor] = useState<boolean>(false);
  // end: CKEditor 関連。

  /** 追加・編集状態。 */
  const isAddingOrEditing = mode === 'Add' || mode === 'Edit';

  // ユーザー情報を取得
  const defaultOptions = useDefaultOptions(i18n.language);
  const loginUserInfo = useGetLoginUserInfo(defaultOptions);
  const loginUserCompanyId: number = _.get(loginUserInfo, 'data.data.company_id', 0);

  // ps情報を取得
  const productionInfo = useContext(PsContext);
  const productionMakerCompanyId: number = _.get(productionInfo, 'create_user.company_id', 0);

  // 原作メーカーのユーザーかどうか
  const adminCompanyIds = [1, 2];
  const isOwnProduction =
    (loginUserCompanyId > 0 && productionMakerCompanyId > 0 && loginUserCompanyId === productionMakerCompanyId) || // 原作メーカーの所属社員である
    adminCompanyIds.includes(loginUserCompanyId) === true; // 管理ユーザーはいつでも権限をもつ

  /** Diffダイアログの表示／非表示 */
  const [isDiffDlgOpen, setIsDiffDlgOpen] = useState(false);

  /** Diffタイトル */
  const [diffTitle, setDiffTitle] = useState('');

  /** Diffダイアログの言語ID */
  const [languageId, setLanguageId] = useState(-1);

  /** 現在のPS */
  const [psValue, setPsValue] = useState<string | object | undefined>('');

  /** 一つ前のPS */
  const [psOldValue, setPsOldValue] = useState<string | object | undefined>('');

  // #region コンテキストメニュー
  const { points, setPoints } = useContextMenu();
  const [contextOpen, setContextOpen] = useState(false);
  const ref = useRef(null);
  useClickAway(ref, () => {
    setContextOpen(false);
  });
  const [contextUp, setContextUp] = useState(false);
  const useWindowSize = (): number[] => {
    const [size, setSize] = useState([0, 0]);
    useLayoutEffect(() => {
      const updateSize = (): void => {
        setSize([window.innerWidth, window.innerHeight]);
      };

      window.addEventListener('resize', updateSize);
      updateSize();

      return () => window.removeEventListener('resize', updateSize);
    }, []);
    return size;
  };
  const [width, height] = useWindowSize();
  const handleContextMenu = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    e.preventDefault();
    setContextOpen(true);
  };
  // #endregion

  return (
    <td
      style={{
        backgroundColor: getDiffBackgroundColor(
          psDerivationContentIdDiffTypeMap,
          (getValues(derivationContentName) as PsDerivationContent).derivation_content_id as number
        ),
      }}
    >
      {isAddingOrEditing && (
        // PS 追加・編集画面の場合。
        // 記載不要チェックボックス。
        // チェックを入れると、該当派生コンテンツが編集不可となり、「記載不要」という文字がセットされる。
        <div
          style={{
            marginBottom: '0.5rem',
          }}
        >
          <CheckBox name={`${derivationContentName}.unnecessary`} register={register} label={t('記載不要')} />
        </div>
      )}
      {!isAddingOrEditing &&
        // PS 閲覧・比較画面の場合。
        // ps_derivation_contents.represent = 1 の場合、「代表例」アイコンを表示。
        Boolean(getValues(`${derivationContentName}.represent`)) && (
          <span className="daihyou">
            <i>{t('代表例')}</i>{' '}
          </span>
        )}
      {
        // ps_derivation_contents.unnecessary でない場合。
        !unnecessary && (
          <>
            {typeof contentXml === 'string' &&
              // 表が存在する場合 (table タグが XML に含まれる場合) は表アイコンを、表示テキストの先頭に表示する。
              // RegExp で大文字・小文字を区別しない検索を行う。
              /<table\b/i.test(contentXml) && (
                <>
                  <img
                    alt="[table icon]"
                    style={{
                      maxWidth: '2rem',
                      maxHeight: '2rem',
                    }}
                    src="/images_ps/table.svg"
                  />
                  <br />
                </>
              )}
            {typeof contentXml === 'string' &&
              // イラストが存在する場合 (img タグが XML に含まれる場合)、含まれる実際の画像を、複数ある場合はすべて固定サイズ (縮小、拡大して表示される) で、テキストの先頭に表示する。
              // コンテンツ XML から img タグを RegExp で全て抽出。
              // 大文字・小文字を区別しない検索を行う。
              Array.from(contentXml.matchAll(/<img\b[^>]*>/gi)).map(
                (imgTagRegExpMatchArray: RegExpMatchArray, imgTagRegExpMatchArrayIndex: number) => {
                  // console.log('imgTagRegExpMatchArray: ', imgTagRegExpMatchArray);

                  // 抽出された各 img タグから src 属性を抽出。
                  // src 属性値を Capturing group として取得。[2] か [3] に入っているはず。
                  // https://regex101.com/ などで確認可能。
                  const srcRegExpMatchArray = imgTagRegExpMatchArray[0].match(/\bsrc=("([^"]*)"|'([^']*)')/i);

                  // console.log('srcRegExpMatchArray: ', srcRegExpMatchArray);

                  const srcValue =
                    Array.isArray(srcRegExpMatchArray) && srcRegExpMatchArray.length >= 3
                      ? // [2] か [3] どちらかには入っているはず。
                        srcRegExpMatchArray[2] || srcRegExpMatchArray[3]
                      : '';

                  // console.log('srcValue: ', srcValue);

                  return (
                    // key={srcValue} だと「Warning: Encountered XXX children with the same key」となることがあるが、content_xml に同じ画像が複数ある場合、避けようがないか。
                    // TODO: 確認。同じ画像が複数あった場合、1 つだけでいい？
                    <React.Fragment
                      key={srcValue + toString(imgTagRegExpMatchArrayIndex)}
                      // key={srcValue}
                    >
                      <img
                        src={srcValue.replace('&amp;', '&')}
                        alt="[illust]"
                        style={{
                          // 縦横比を維持しつつ固定サイズ (上限) で表示。
                          maxWidth: '2rem',
                          maxHeight: '2rem',
                        }}
                      />
                      <br />
                    </React.Fragment>
                  );
                }
              )}
          </>
        )
      }
      <div
        onClick={() => {
          if (mode === 'Diff' && !contextOpen) {
            const derivationContentId = Number(
              (getValues(derivationContentName) as PsDerivationContent).derivation_content_id
            );

            let newValue = '';
            if (psDerivationContentIdDiffTypeMap.get(derivationContentId) !== 'Deleted') {
              newValue = String(contentXmlEdit);
            }
            const oldValue = psDerivationContentIdDiffXmlMap.get(
              (getValues(derivationContentName) as PsDerivationContent).derivation_content_id as number
            );
            if (newValue !== oldValue) {
              setDiffTitle('PS XML');
              setLanguageId(derivationContentIndex);
              setPsValue(newValue);
              setPsOldValue(oldValue);
              setIsDiffDlgOpen(true);
            }
          } else {
            // 派生コンテンツのテキストをクリックすると、CKEditor をダイアログとして表示する。
            // 記載不要の場合、CKEditor を呼び出さない。
            setDoShowCKEditor(!unnecessary && !doShowCKEditor);
          }
        }}
        style={{
          // content_text が空でもクリックできるようにする。
          width: '100%',
          minHeight: '4rem',
          // 「記載不要」でなければポインターで押下できることを表す。
          cursor: unnecessary ? 'inherit' : 'pointer',
          // 「記載不要」でなければ押下可能範囲に枠線を表示する。
          border: unnecessary ? 'none' : '1px solid lightgray',
          // CKEditorダイアログの外側をクリックして閉じるように改修した際、派生コンテンツのテキストだけはクリック可能とするため。
          position: 'relative',
          zIndex: 101,
        }}
        onContextMenu={(event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
          if (mode === 'Diff') {
            event.preventDefault();
            if (height - event.clientY < 220) {
              setContextUp(true);
              setPoints({ x: event.clientX, y: height - event.clientY + 5 });
            } else {
              setPoints({ x: event.clientX, y: event.clientY + 5 });
            }
            handleContextMenu(event);
          }
        }}
      >
        {
          // ps_derivation_contents.unnecessary = 1 の場合、「記載不要」と表示。
          // 記載不要チェックボックスを押下した時も「記載不要」と表示されることになる。
          unnecessary
            ? t('記載不要')
            : // 派生コンテンツ XML から style 属性を含む span 要素だけ残し、他のタグは取り除いたテキストを表示する (getPs の戻り値に含まれる content_text)。
              typeof contentText === 'string' && (
                <div
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: contentText }}
                />
              )
        }
        <ul
          className="mt-popup"
          style={{
            display: contextOpen ? 'block' : '',
            top: contextUp ? '' : `${points.y}px`,
            bottom: contextUp ? `${points.y}px` : '',
            left: `${points.x}px`,
          }}
          ref={ref}
        >
          <li>
            <i className="fa-solid fa-eye" />
            {t('閲覧')}
          </li>
        </ul>
      </div>

      {/* CKEditor のダイアログ。 */}
      {
        // ダイアログ表示時に CKEditor を初めて描画するようにしないと、遅くなる (描画しておくが非表示にしておく処理だとリソースを食う) ようだ。
        doShowCKEditor && loginUserInfo.data?.data && (
          // ドラッグ可能、背後にアクセス可能な (モードレス) ダイアログ。
          // PsDetails で DraggableModelessDialogsProvider で囲んでいるため、複数表示でも表示順序を管理可能。
          <DraggableModelessDialog
            isOpen={doShowCKEditor}
            onClose={() => {
              setDoShowCKEditor(false);
            }}
            // 保存ボタンを渡す。
            headerContents={
              isAddingOrEditing && (
                <div
                  style={{
                    display: 'flex',
                    // 保存ボタンの表示箇所を justifyContent で変更可能。
                    // justifyContent: 'center',
                    justifyContent: 'flex-start',
                    width: '100%',
                  }}
                >
                  <button
                    type="button"
                    style={{
                      cursor: 'pointer',
                      background: 'none',
                      textAlign: 'center',
                      borderRight: '1px solid lightgray',
                      borderLeft: 'unset',
                      borderTop: 'unset',
                      borderBottom: 'unset',
                      minWidth: '6em',
                      // borderRadius: '0.5em',
                    }}
                    onClick={async () => {
                      // console.log('content_xml 保存 (postPsContent API 呼び出し):');

                      // console.log('editor:', editor);

                      if (editor) {
                        // editor からデータを取得する。
                        const data = editor.data.get();

                        // console.log('editor.data.get() (CKEditor から取得したデータ):', data);
                        // console.log('content_xml_edit:', contentXmlEdit);

                        await psCheckMutateAsync(
                          {
                            psVersionId,
                            data: { content_xml: data, ps_type: psType },
                          },
                          {
                            onSuccess: () => {
                              // 成功したらckeditorの値を反映
                              postPsContentMutation.mutate({
                                psVersionId,
                                data: { content_xml: data, ps_type: psType },
                              });

                              if (postPsContentMutation.status === 'success' && postPsContentMutation.data?.data) {
                                // console.log('point1で閉じる');
                                setDoShowCKEditor(false);
                              }
                            },
                            onError: () => {
                              // console.log('問題ありで閉じない');
                            },
                          }
                        );
                      } else {
                        // ckeditor非表示
                        setDoShowCKEditor(false);
                      }
                    }}
                    disabled={!editor}
                  >
                    {t('保存')}
                  </button>
                </div>
              )
            }
            initialWidth={isAddingOrEditing ? 850 : 818}
            initialHeight={600}
            id={derivationContentName}
          >
            <CKEditorAdapter
              toolbar={
                isAddingOrEditing
                  ? {
                      shouldNotGroupWhenFull: true,
                      items: [
                        'bold',
                        'italic',
                        'underline',
                        'fontColor',
                        'subscript',
                        'superscript',
                        'specialCharacters',
                        'link',
                        // 'bulletedList',
                        // 'numberedList',
                        'customItalicButton',
                        'customULButton',
                        'customOLButton',
                        'customOLTextStart',
                        '|',
                        'outdent',
                        'indent',
                        'alignment',
                        'pageBreak',
                        '|',
                        'blockQuote',
                        'insertTable',
                        'undo',
                        'redo',
                        '-',
                        'CustomImageChoosingButton',
                        'sourceEditing',
                        '|',
                        'heading',
                        'style',
                        '|',
                        'insertTemplate',
                        '|',
                        'customButtonBlock',
                        'customButtonLBlock',
                        'customButtonPsCopy',
                        'customButtonPsPaste',
                      ],
                    }
                  : {
                      shouldNotGroupWhenFull: true,
                      // items まで指定しないと反映されないようだ。
                      items: ['customButtonPsCopy'],
                    }
              }
              isReadOnly={!isAddingOrEditing}
              isOwnProduction={isOwnProduction}
              // データを渡す。
              initialData={contentXmlEdit}
              // editor を取得し、state に set する。
              setEditor={(editorToSet: Editor) => {
                // console.log('editorToSet:', editorToSet);

                setEditor(editorToSet);
              }}
              // onBlur で editor を取得し、editor.data.get() で content_xml を更新。
              // onBlur={
              //   (editor: Editor) => {
              //     console.log('onBlur:', editor);

              //     setValue(
              //       `${derivationContentName}.content_xml` as never,
              //       editor.data.get() as never,
              //       { shouldValidate: true, shouldDirty: true, shouldTouch: true }
              //     );
              //   }
              // }
              psVersionId={psVersionId}
              user={loginUserInfo.data.data}
              psNo={psNo}
              psDerivationId={psDerivationId}
              fsrId={fsrId}
              psType={psType}
              psLevel={psLevel}
              languageId={derivationContentIndex}
              setApiProcessing={setApiProcessing} // api処理中フラグ
            />
          </DraggableModelessDialog>
        )
      }

      {/* XML比較ダイアログ */}
      {isDiffDlgOpen && (
        <PsDiffViewerDialog
          isOpen={isDiffDlgOpen}
          setIsOpen={setIsDiffDlgOpen}
          titleText={diffTitle}
          langugeId={languageId}
          oldText={psOldValue}
          newText={psValue}
          oldTitle="変更前"
          newTitle="変更後"
          isXml
        />
      )}
      {
        // PS コンテンツ変換 API のエラー メッセージを表示。
        postPsContentMutation.isError && (
          <ErrorModal error={postPsContentMutation.error}>
            {t('PSコンテンツ変換に失敗しました。管理者に打ち上げください。')}
            <br />
            <br />
          </ErrorModal>
        )
      }
      {/* スピナーモーダル */}
      {checkIsLoding && <SpinnerModal />}
      {/* 原稿ダウンロード前チェックエラー */}
      {displayCheckError && (
        <ErrorModal
          error={checkError}
          onClickOk={() => {
            setDisplayCheckError(false);
          }}
        />
      )}
      {apiProcessing && <SpinnerModal />}
    </td>
  );
}

export default PsDerivationContentXmlCell;
